import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';

import { ACTION_SX, BRAND, C, sayiyaCevir } from './teklifTheme';

const GRUP_IKON = {
  MALZEME: <Inventory2OutlinedIcon fontSize="small" />,
  ISCILIK: <EngineeringOutlinedIcon fontSize="small" />,
  DIGER: <MoreHorizIcon fontSize="small" />,
  ESKI: <HistoryIcon fontSize="small" />
};

const nf = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const styles = {
  headCell: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    padding: '10px 12px'
  },
  bodyCell: {
    padding: '6px 12px',
    fontSize: '0.875rem',
    color: C.inkBody,
    borderBottom: `1px solid ${C.borderSoft}`
  },
  grupCell: {
    padding: '8px 12px',
    backgroundColor: '#fff',
    borderBottom: `1px solid ${C.border}`,
    borderTop: `1px solid ${C.border}`,
    cursor: 'pointer',
    userSelect: 'none'
  },
  grupIkon: {
    width: 28,
    height: 28,
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: `${BRAND.main}1f`,
    color: BRAND.main,
    flexShrink: 0
  },
  miktar: {
    width: 104,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: C.surface,
      '& fieldset': { borderColor: C.border },
      '&.Mui-focused fieldset': { borderColor: BRAND.main }
    },
    '& input': { textAlign: 'right', padding: '6px 10px' }
  },
  birim: {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: C.headBg,
    color: C.inkMuted
  }
};

// Satır tutarı grup indirimi düşülüp kuruşa yuvarlanır (SP ile aynı)
const netTutar = (tl, miktar, oran) =>
  Math.round(tl * miktar * (100 - oran)) / 100;

const ara = (metin, q) =>
  String(metin || '')
    .toLocaleLowerCase('tr-TR')
    .includes(q);

/**
 * Seçili fiyat listesinin kalemleri gruplu gösterilir; miktar girilen kalem teklife girer.
 * satir: { id, grup, tanim, birim, fiyat, pb, adet, tl, kurYok, donmus, guncelFiyat, yok }
 */
function FiyatListesiKalemSecimi({
  gruplar,
  eskiSatirlar,
  miktarlar,
  paraBirimi,
  goster,
  listeSecili,
  yukleniyor,
  onMiktar,
  onSec,
  onEskiMiktar,
  onEskiSil,
  grupIndirimleri,
  onGrupIndirim
}) {
  const [arama, setArama] = useState('');
  const [sadeceSecili, setSadeceSecili] = useState(false);
  const [kapali, setKapali] = useState({});

  const seciliMi = (id) => sayiyaCevir(miktarlar[id]) > 0;
  const oran = (kod) =>
    Math.min(Math.max(sayiyaCevir(grupIndirimleri[kod]), 0), 100);
  const q = arama.trim().toLocaleLowerCase('tr-TR');
  const seciliSayisi = Object.keys(miktarlar).filter(
    (id) => sayiyaCevir(miktarlar[id]) > 0
  ).length;

  const gorunen = useMemo(
    () =>
      gruplar
        .map((g) => ({
          ...g,
          tumu: g.satirlar.length,
          secili: g.satirlar.filter((s) => sayiyaCevir(miktarlar[s.id]) > 0)
            .length,
          toplam: g.satirlar.reduce(
            (t, s) =>
              t +
              netTutar(
                s.tl,
                sayiyaCevir(miktarlar[s.id]),
                Math.min(Math.max(sayiyaCevir(grupIndirimleri[g.kod]), 0), 100)
              ),
            0
          ),
          satirlar: g.satirlar.filter(
            (s) =>
              (!q || ara(s.tanim, q)) &&
              (!sadeceSecili || sayiyaCevir(miktarlar[s.id]) > 0)
          )
        }))
        .filter((g) => g.satirlar.length),
    [gruplar, miktarlar, q, sadeceSecili, grupIndirimleri]
  );

  const grupBasligi = (kod, ad, sag, acik, onClick) => (
    <TableRow key={`g-${kod}`}>
      <TableCell colSpan={6} sx={styles.grupCell} onClick={onClick}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: C.inkMuted,
              transform: acik ? 'none' : 'rotate(-90deg)',
              transition: 'transform .15s'
            }}
          />
          <Box sx={styles.grupIkon}>{GRUP_IKON[kod]}</Box>
          <Typography
            sx={{ fontWeight: 700, color: C.ink, fontSize: '0.875rem' }}
          >
            {ad}
          </Typography>
          {sag}
        </Box>
      </TableCell>
    </TableRow>
  );

  const fiyatHucresi = (s) => {
    if (s.yok)
      return (
        <Typography
          component="span"
          sx={{ color: BRAND.danger, fontSize: '0.75rem' }}
        >
          Listede yok
        </Typography>
      );
    if (s.kurYok)
      return (
        <Tooltip
          title={`${s.pb} kuru alınamadı. Teklif para birimini ${s.pb} seçip kuru elle girin.`}
          arrow
        >
          <Typography
            component="span"
            sx={{ color: BRAND.danger, fontSize: '0.75rem' }}
          >
            {nf.format(s.fiyat)} {s.pb} · kur yok
          </Typography>
        </Tooltip>
      );
    return (
      <Box>
        <Typography
          component="div"
          sx={{ fontSize: '0.875rem', color: C.inkBody }}
        >
          {goster(s.tl)}
        </Typography>
        {(s.pb !== paraBirimi || s.guncelFiyat) && (
          <Tooltip
            title={
              s.guncelFiyat
                ? `Teklife eklendiği fiyat korunuyor. Listedeki güncel fiyat: ${s.guncelFiyat}`
                : ''
            }
            arrow
          >
            <Typography
              component="div"
              sx={{ fontSize: '0.7rem', color: C.inkMuted }}
            >
              {[
                s.pb !== paraBirimi && `${nf.format(s.fiyat)} ${s.pb}`,
                s.guncelFiyat && 'kayıtlı fiyat'
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };

  let govde;
  if (!listeSecili && !eskiSatirlar.length) {
    govde = 'Teklif kalemleri için önce yukarıdan fiyat listesi seçin.';
  } else if (yukleniyor) {
    govde = <CircularProgress size={22} sx={{ color: BRAND.main }} />;
  } else if (!gorunen.length && !eskiSatirlar.length) {
    govde =
      q || sadeceSecili
        ? 'Aramaya uyan kalem yok.'
        : 'Bu fiyat listesinde aktif kalem yok.';
  }

  return (
    <Box>
      {listeSecili && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.5
          }}
        >
          <TextField
            size="small"
            placeholder="Kalem ara"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: C.inkSoft }} />
                </InputAdornment>
              )
            }}
            sx={{
              ...styles.miktar,
              width: { xs: '100%', sm: 300 },
              '& input': {}
            }}
          />
          <Chip
            clickable
            onClick={() => setSadeceSecili((v) => !v)}
            variant={sadeceSecili ? 'filled' : 'outlined'}
            label={`Sadece seçilenler · ${seciliSayisi}`}
            sx={
              sadeceSecili
                ? {
                    backgroundColor: `${BRAND.main}1f`,
                    color: '#9a5b00',
                    fontWeight: 700
                  }
                : { color: C.inkBody }
            }
          />
          <Typography variant="body2" sx={{ color: C.inkMuted, ml: 'auto' }}>
            Miktar girdiğiniz kalemler teklife eklenir.
          </Typography>
        </Box>
      )}

      <TableContainer
        sx={{
          maxHeight: 520,
          border: `1px solid ${C.border}`,
          borderRadius: '12px'
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ ...styles.headCell, width: 44 }}
                padding="checkbox"
              />
              <TableCell sx={styles.headCell}>İş Kalemi</TableCell>
              <TableCell sx={{ ...styles.headCell, width: 150 }} align="right">
                Birim Fiyat
              </TableCell>
              <TableCell sx={{ ...styles.headCell, width: 90 }} align="center">
                Birim
              </TableCell>
              <TableCell sx={{ ...styles.headCell, width: 120 }} align="right">
                Miktar
              </TableCell>
              <TableCell sx={{ ...styles.headCell, width: 140 }} align="right">
                Tutar
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {govde ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    ...styles.bodyCell,
                    textAlign: 'center',
                    py: 4,
                    color: C.inkSoft
                  }}
                >
                  {govde}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {eskiSatirlar.length > 0 && (
                  <>
                    {grupBasligi(
                      'ESKI',
                      'Önceki katalog kalemleri',
                      <Chip
                        size="small"
                        label={eskiSatirlar.length}
                        sx={{ height: 20 }}
                      />,
                      !kapali.ESKI,
                      () => setKapali((k) => ({ ...k, ESKI: !k.ESKI }))
                    )}
                    {!kapali.ESKI &&
                      eskiSatirlar.map((s) => (
                        <TableRow key={s.SatirId}>
                          <TableCell sx={styles.bodyCell} padding="checkbox">
                            <Tooltip title="Satırı sil" arrow>
                              <IconButton
                                size="small"
                                onClick={() => onEskiSil(s.SatirId)}
                                sx={ACTION_SX.danger}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={styles.bodyCell}>{s.tanim}</TableCell>
                          <TableCell sx={styles.bodyCell} align="right">
                            {goster(s.tl)}
                          </TableCell>
                          <TableCell sx={styles.bodyCell} align="center">
                            {s.birim && (
                              <Chip
                                size="small"
                                label={s.birim}
                                sx={styles.birim}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={styles.bodyCell} align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={s.Miktar}
                              onChange={(e) =>
                                onEskiMiktar(s.SatirId, e.target.value)
                              }
                              inputProps={{
                                min: 0,
                                step: 'any',
                                'aria-label': 'Miktar'
                              }}
                              sx={styles.miktar}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              ...styles.bodyCell,
                              fontWeight: 700,
                              color: C.ink
                            }}
                            align="right"
                          >
                            {goster(s.tl * sayiyaCevir(s.Miktar))}
                          </TableCell>
                        </TableRow>
                      ))}
                  </>
                )}

                {gorunen.map((g) => {
                  const acik = Boolean(q) || !kapali[g.kod];
                  return (
                    <React.Fragment key={g.kod}>
                      {grupBasligi(
                        g.kod,
                        g.ad,
                        <>
                          <Chip
                            size="small"
                            label={
                              g.secili
                                ? `${g.secili} / ${g.tumu} seçili`
                                : `${g.tumu} kalem`
                            }
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              ...(g.secili && {
                                backgroundColor: `${BRAND.main}1f`,
                                color: '#9a5b00',
                                fontWeight: 700
                              })
                            }}
                          />
                          <TextField
                            size="small"
                            type="number"
                            placeholder="İndirim"
                            value={grupIndirimleri[g.kod] ?? ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              onGrupIndirim(g.kod, e.target.value)
                            }
                            error={
                              sayiyaCevir(grupIndirimleri[g.kod]) < 0 ||
                              sayiyaCevir(grupIndirimleri[g.kod]) > 100
                            }
                            inputProps={{
                              min: 0,
                              max: 100,
                              step: 'any',
                              'aria-label': `${g.ad} indirimi`
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  %
                                </InputAdornment>
                              )
                            }}
                            sx={{ ...styles.miktar, width: 136, ml: 0.5 }}
                          />
                          {g.secili > 0 && (
                            <Typography
                              sx={{
                                ml: 'auto',
                                fontWeight: 700,
                                color: C.ink,
                                fontSize: '0.875rem'
                              }}
                            >
                              {goster(g.toplam)}
                            </Typography>
                          )}
                        </>,
                        acik,
                        () => setKapali((k) => ({ ...k, [g.kod]: !k[g.kod] }))
                      )}
                      {acik &&
                        g.satirlar.map((s) => {
                          const secili = seciliMi(s.id);
                          const pasif = s.yok || s.kurYok;
                          return (
                            <TableRow
                              key={s.id}
                              sx={{
                                backgroundColor: secili
                                  ? `${BRAND.main}0d`
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: secili
                                    ? `${BRAND.main}17`
                                    : C.headBg
                                }
                              }}
                            >
                              <TableCell
                                sx={styles.bodyCell}
                                padding="checkbox"
                              >
                                <Checkbox
                                  size="small"
                                  checked={secili}
                                  disabled={pasif && !secili}
                                  onChange={(e) => onSec(s, e.target.checked)}
                                  inputProps={{ 'aria-label': s.tanim }}
                                  sx={{
                                    '&.Mui-checked': { color: BRAND.main }
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...styles.bodyCell,
                                  color: secili ? C.ink : C.inkBody,
                                  fontWeight: secili ? 600 : 400,
                                  ...(s.yok && {
                                    textDecoration: 'line-through',
                                    color: C.inkSoft
                                  })
                                }}
                              >
                                {s.tanim}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...styles.bodyCell,
                                  whiteSpace: 'nowrap'
                                }}
                                align="right"
                              >
                                {fiyatHucresi(s)}
                              </TableCell>
                              <TableCell sx={styles.bodyCell} align="center">
                                {s.birim && (
                                  <Chip
                                    size="small"
                                    label={s.birim}
                                    sx={styles.birim}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={styles.bodyCell} align="right">
                                <TextField
                                  size="small"
                                  type="number"
                                  placeholder="0"
                                  value={miktarlar[s.id] ?? ''}
                                  disabled={pasif && !secili}
                                  onChange={(e) =>
                                    onMiktar(s.id, e.target.value)
                                  }
                                  inputProps={{
                                    min: 0,
                                    step: 'any',
                                    'aria-label': `${s.tanim} miktarı`
                                  }}
                                  sx={styles.miktar}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...styles.bodyCell,
                                  whiteSpace: 'nowrap',
                                  fontWeight: secili ? 700 : 400,
                                  color: secili ? C.ink : C.inkSoft
                                }}
                                align="right"
                              >
                                {secili ? (
                                  <>
                                    {goster(
                                      netTutar(
                                        s.tl,
                                        sayiyaCevir(miktarlar[s.id]),
                                        oran(g.kod)
                                      )
                                    )}
                                    {oran(g.kod) > 0 && (
                                      <Typography
                                        component="div"
                                        sx={{
                                          fontSize: '0.7rem',
                                          color: C.inkSoft,
                                          fontWeight: 400,
                                          textDecoration: 'line-through'
                                        }}
                                      >
                                        {goster(
                                          s.tl * sayiyaCevir(miktarlar[s.id])
                                        )}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

FiyatListesiKalemSecimi.propTypes = {
  gruplar: PropTypes.array.isRequired,
  eskiSatirlar: PropTypes.array.isRequired,
  miktarlar: PropTypes.object.isRequired,
  paraBirimi: PropTypes.string.isRequired,
  goster: PropTypes.func.isRequired,
  listeSecili: PropTypes.bool,
  yukleniyor: PropTypes.bool,
  onMiktar: PropTypes.func.isRequired,
  onSec: PropTypes.func.isRequired,
  onEskiMiktar: PropTypes.func.isRequired,
  onEskiSil: PropTypes.func.isRequired,
  grupIndirimleri: PropTypes.object,
  onGrupIndirim: PropTypes.func.isRequired
};

FiyatListesiKalemSecimi.defaultProps = {
  grupIndirimleri: {}
};

export default FiyatListesiKalemSecimi;
